import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CalendarIcon, MapPinIcon, ClockIcon, DollarSignIcon, ShoppingCartIcon, EyeIcon, ClipboardListIcon, XIcon, PackageIcon, UserIcon, FilterIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon, BanIcon } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { StatsCard } from '../components/ui/StatsCard';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { Visit } from '../types';
import { useSortableData } from '../hooks/useSortableData';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import { supabaseAdmin } from '../lib/supabase';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
const mapVisitLite = (row: any): Visit => ({
  id: row.id,
  vendedorId: row.vendedor_id,
  vendedorNombre: row.vendedor_nombre,
  clienteId: row.cliente_id,
  clienteNombre: row.cliente_nombre,
  zona: row.zona,
  tipoVisita: row.tipo_visita,
  fecha: new Date(row.fecha),
  horaLlegada: row.hora_llegada ? new Date(row.hora_llegada) : null,
  horaSalida: row.hora_salida ? new Date(row.hora_salida) : null,
  tiempoTotal: row.tiempo_total,
  cobro: {
    huboCobo: false,
    montoCobrado: 0,
    metodoPago: null,
    numeroFactura: '',
    estadoFactura: 'no_pago',
    credito: 0,
    descuento: 0,
    ...(row.cobro || {})
  },
  venta: {
    huboVenta: false,
    montoVenta: 0,
    numeroPedido: '',
    ...(row.venta || {})
  },
  comentarios: row.comentarios || '',
  motivoNoPago: row.motivo_no_pago || '',
  productosVencidos: row.productos_vencidos ?? null,
  devolucionItems: [],
  proximaVisita: row.proxima_visita ? new Date(row.proxima_visita) : null,
  estado: row.estado,
  numeroRecibo: row.numero_recibo || null
});
export function VisitsPage() {
  const {
    user
  } = useAuth();
  const {
    users
  } = useApp();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const [visits, setVisits] = useState<Visit[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 8;
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── SERVER-SIDE FILTERING + PAGINATION ───────────────────────────────────
  // Filters are sent to Supabase — no client-side re-filtering needed.
  // This replaces downloading 2 000 rows and filtering in JS.
  const fetchVisits = useCallback(async (page: number, date: string, status: string, vendor: string, type: string) => {
    setIsLoading(true);
    try {
      const COLS = 'id,vendedor_id,vendedor_nombre,cliente_id,cliente_nombre,zona,tipo_visita,fecha,hora_llegada,hora_salida,tiempo_total,cobro,venta,comentarios,motivo_no_pago,productos_vencidos,proxima_visita,estado,numero_recibo,created_at';
      const rangeFrom = (page - 1) * ITEMS_PER_PAGE;
      const rangeTo = rangeFrom + ITEMS_PER_PAGE - 1;
      let q = supabaseAdmin.from('visits_lite').select(COLS, {
        count: 'exact'
      }).order('fecha', {
        ascending: false
      }).range(rangeFrom, rangeTo);
      // Date filter: scope to the exact day (DR timezone offset UTC-4)
      if (date) {
        q = q.gte('fecha', `${date}T04:00:00.000Z`).lt('fecha', `${date}T28:00:00.000Z`);
      } else {
        // Default window: last 365 days
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - 90);
        q = q.gte('fecha', cutoff.toISOString());
      }
      if (status) q = q.eq('estado', status);
      if (type) q = q.eq('tipo_visita', type);
      // Vendor scoping — non-admins always see only their own visits
      if (vendor) q = q.eq('vendedor_id', vendor);else if (!isAdmin && user?.id) q = q.eq('vendedor_id', user.id);
      const {
        data,
        error,
        count
      } = await q;
      if (error) {
        console.error('[VisitsPage] fetch error:', error);
        setVisits([]);
        setTotalCount(0);
      } else {
        setVisits((data || []).map(mapVisitLite));
        setTotalCount(count ?? 0);
      }
    } catch (err) {
      console.error('[VisitsPage] fetch exception:', err);
      setVisits([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin, user?.id]);

  // Re-fetch (debounced 300 ms) whenever filters or page change
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchVisits(currentPage, dateFilter, statusFilter, vendorFilter, typeFilter);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [currentPage, dateFilter, statusFilter, vendorFilter, typeFilter, fetchVisits]);

  // Realtime: reload current page when remote changes arrive
  useRealtimeRefresh(['visits'], () => fetchVisits(currentPage, dateFilter, statusFilter, vendorFilter, typeFilter));

  // Server already filtered — no client re-filter required
  const filteredVisits = visits;
  // ── Retroactive discount map (same logic as CollectionsPage) ──────────────
  const [retroDiscountMap, setRetroDiscountMap] = useState<Record<string, number>>({});
  useEffect(() => {
    // Debounce: wait 800ms after visits change before computing
    const timer = setTimeout(() => {
      const computeRetroDiscounts = async () => {
        // Find cobro visits where descuento === 0 but may have PP% on invoices
        const cobroVisits = filteredVisits.filter(v => (v.tipoVisita === 'cobro' || v.tipoVisita === 'cobro_y_venta') && v.cobro.huboCobo && v.cobro.montoCobrado > 0 && (v.cobro.descuento || 0) === 0 && !(v.cobro as any)?.anulado);
        if (cobroVisits.length === 0) {
          setRetroDiscountMap({});
          return;
        }
        // Build map of visitId → invoice numbers
        const visitFactMap: Record<string, string[]> = {};
        const allNums: string[] = [];
        cobroVisits.forEach(v => {
          const fd: any[] = (v.cobro as any).facturasDetalle || [];
          const nums = fd.length > 0 ? fd.map((f: any) => f.numeroFactura).filter(Boolean) : v.cobro.numeroFactura ? v.cobro.numeroFactura.split(',').map((n: string) => n.trim()).filter(Boolean) : [];
          if (nums.length > 0) {
            visitFactMap[v.id] = nums;
            allNums.push(...nums);
          }
        });
        const uniqueNums = [...new Set(allNums)];
        if (uniqueNums.length === 0) return;
        try {
          const {
            data: invRows
          } = await supabaseAdmin.from('invoices').select('numero_factura, pp_pct, sub_total').in('numero_factura', uniqueNums.slice(0, 200));
          if (!invRows) return;
          const ppMap: Record<string, {
            ppPct: number | null;
            subTotal: number;
          }> = {};
          invRows.forEach((row: any) => {
            ppMap[row.numero_factura] = {
              ppPct: row.pp_pct != null ? Number(row.pp_pct) : null,
              subTotal: Number(row.sub_total) || 0
            };
          });
          const newMap: Record<string, number> = {};
          Object.entries(visitFactMap).forEach(([visitId, nums]) => {
            const v = filteredVisits.find(vis => vis.id === visitId);
            if (!v) return;
            const fd: any[] = (v.cobro as any).facturasDetalle || [];
            let totalDisc = 0;
            nums.forEach(num => {
              const pp = ppMap[num];
              const savedDesc = fd.find((f: any) => f.numeroFactura === num)?.descuento || 0;
              if (savedDesc === 0 && pp && pp.ppPct != null && pp.ppPct > 0 && pp.subTotal > 0) {
                totalDisc += Math.round(pp.subTotal * (pp.ppPct / 100) * 100) / 100;
              }
            });
            if (totalDisc > 0) newMap[visitId] = totalDisc;
          });
          setRetroDiscountMap(newMap);
        } catch {

          // Silently fail
        }
      };
      computeRetroDiscounts();
    }, 800);
    return () => clearTimeout(timer);
  }, [filteredVisits]);
  // Helper: get effective cobro amount (accounting for discount)
  const getEffectiveCobro = (visit: Visit): number => {
    if ((visit.cobro as any)?.anulado) return 0;
    if (!visit.cobro.huboCobo || visit.cobro.montoCobrado <= 0) return 0;
    const savedDesc = visit.cobro.descuento || 0;
    if (savedDesc > 0) {
      // Discount was saved at visit time — montoCobrado should already be after discount
      // but if montoCobrado equals credito, it wasn't adjusted, so subtract
      const credito = visit.cobro.credito || 0;
      if (credito > 0 && Math.abs(visit.cobro.montoCobrado - credito) < 0.01) {
        // montoCobrado was NOT adjusted — subtract descuento
        return Math.round((visit.cobro.montoCobrado - savedDesc) * 100) / 100;
      }
      return visit.cobro.montoCobrado;
    }
    // Check retroactive discount
    const retroDisc = retroDiscountMap[visit.id] || 0;
    if (retroDisc > 0) {
      return Math.round((visit.cobro.montoCobrado - retroDisc) * 100) / 100;
    }
    return visit.cobro.montoCobrado;
  };
  // Helper: get total discount for a visit (saved or retroactive)
  const getVisitDiscount = (visit: Visit): number => {
    const savedDesc = visit.cobro.descuento || 0;
    if (savedDesc > 0) return savedDesc;
    return retroDiscountMap[visit.id] || 0;
  };
  // Get active vendors for filter (non-admin users)
  const vendors = users.filter(u => u.rolNombre !== 'Administrador' && u.activo && u.permisos?.['new-visit'] === true);
  const vendorOptions = [{
    value: '',
    label: 'Todos los vendedores'
  }, ...vendors.map(v => ({
    value: v.id,
    label: v.nombre
  }))];
  const statusOptions = [{
    value: '',
    label: 'Todos los estados'
  }, {
    value: 'completada',
    label: 'Completada'
  }, {
    value: 'en_progreso',
    label: 'En Progreso'
  }, {
    value: 'cancelada',
    label: 'Cancelada'
  }];
  const typeOptions = [{
    value: '',
    label: 'Todos los tipos'
  }, {
    value: 'cobro',
    label: 'Cobro'
  }, {
    value: 'venta',
    label: 'Venta'
  }, {
    value: 'cobro_y_venta',
    label: 'Cobro y Venta'
  }, {
    value: 'seguimiento',
    label: 'Seguimiento'
  }];

  // Sorting setup
  const {
    items: sortedVisits,
    requestSort,
    sortConfig
  } = useSortableData(filteredVisits, {
    fecha: v => new Date(v.fecha).getTime(),
    vendedor: v => v.vendedorNombre,
    cliente: v => v.clienteNombre,
    tipo: v => v.tipoVisita,
    cobro: v => v.cobro.montoCobrado,
    venta: v => v.venta.montoVenta,
    tiempo: v => v.tiempoTotal || 0,
    estado: v => v.estado
  });
  // Default sort by date descending if no sort config
  const finalVisits = sortConfig ? sortedVisits : [...sortedVisits].sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  // Pagination is server-side: data is already the right page
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
  const paginatedVisits = visits; // server already returned the correct slice
  // FIXED: Use Dominican Republic locale and currency
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0
  }).format(amount);
  const formatDate = (date: Date) => new Date(date).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  const formatTime = (date: Date | null) => date ? new Date(date).toLocaleTimeString('es-DO', {
    hour: '2-digit',
    minute: '2-digit'
  }) : '--:--';
  const getVisitTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      cobro: 'Cobro',
      venta: 'Venta',
      cobro_y_venta: 'Cobro y Venta',
      seguimiento: 'Seguimiento'
    };
    return labels[type] || type;
  };
  const getVisitTypeBadge = (type: string) => {
    const config: Record<string, {
      bg: string;
      text: string;
    }> = {
      cobro: {
        bg: 'bg-emerald-100',
        text: 'text-emerald-700'
      },
      venta: {
        bg: 'bg-blue-100',
        text: 'text-blue-700'
      },
      cobro_y_venta: {
        bg: 'bg-indigo-100',
        text: 'text-indigo-700'
      },
      seguimiento: {
        bg: 'bg-amber-100',
        text: 'text-amber-700'
      }
    };
    const style = config[type] || {
      bg: 'bg-slate-100',
      text: 'text-slate-700'
    };
    return <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${style.bg} ${style.text}`} data-id="element-4787" data-mp="h1d0gnsn6cl8">
        
        {getVisitTypeLabel(type)}
      </span>;
  };
  const getStatusBadge = (status: string) => {
    if (status === 'completada') return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-emerald-100 text-emerald-700" data-id="element-4788" data-mp="2nscjrpm6c6ro">
          <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-4789" data-mp="1sj7jq8zw1xgh" />
          Completada
        </span>;
    if (status === 'en_progreso') return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-amber-100 text-amber-700" data-id="element-4790" data-mp="22fu4kxs7soii">
          <ClockIcon className="w-3.5 h-3.5" data-id="element-4791" data-mp="1xhitkjq6o7yp" />
          En Progreso
        </span>;
    return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-rose-100 text-rose-700" data-id="element-4792" data-mp="19resok4dqmbe">
        <XCircleIcon className="w-3.5 h-3.5" data-id="element-4793" data-mp="14pfnd4p2xfbt" />
        Cancelada
      </span>;
  };
  const getInvoiceStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pagada_completa: 'Pagada Completa',
      pago_parcial: 'Pago Parcial',
      no_pago: 'No Pagó'
    };
    return labels[status] || status;
  };
  const getPaymentMethodLabel = (method: string | null) => {
    if (!method) return '-';
    const labels: Record<string, string> = {
      efectivo: 'Efectivo',
      transferencia: 'Transferencia',
      cheque: 'Cheque'
    };
    return labels[method] || method;
  };
  // Stats calculations (exclude anulado cobros, account for discounts)
  const totalCobrado = filteredVisits.reduce((sum, v) => {
    if ((v.cobro as any)?.anulado) return sum;
    return sum + getEffectiveCobro(v);
  }, 0);
  const totalVendido = filteredVisits.reduce((sum, v) => sum + (v.venta.huboVenta ? v.venta.montoVenta : 0), 0);
  const visitasCompletadas = filteredVisits.filter(v => v.estado === 'completada').length;
  const visitasConTiempo = filteredVisits.filter(v => v.tiempoTotal);
  const tiempoPromedio = visitasConTiempo.length > 0 ? Math.round(visitasConTiempo.reduce((sum, v) => sum + (v.tiempoTotal || 0), 0) / visitasConTiempo.length) : 0;
  const clearFilters = () => {
    setDateFilter('');
    setStatusFilter('');
    setVendorFilter('');
    setTypeFilter('');
    setCurrentPage(1);
  };
  const hasActiveFilters = dateFilter || statusFilter || vendorFilter || typeFilter;
  // Render cobro cell with proper logic (includes anulado check + discount)
  const renderCobroCell = (visit: Visit) => {
    const tieneCobroTipo = visit.tipoVisita === 'cobro' || visit.tipoVisita === 'cobro_y_venta';
    if (!tieneCobroTipo) {
      return <span className="text-slate-300" data-id="element-4794" data-mp="3jwtd6wvpaq4x">—</span>;
    }
    // Check if cobro was anulled
    if ((visit.cobro as any)?.anulado) {
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold bg-slate-100 text-slate-500" data-id="element-4795" data-mp="3ewossvro9b9f">
          <BanIcon className="w-3 h-3" data-id="element-4796" data-mp="3a0q04vzb9737" /> Anulado
        </span>;
    }
    if (!visit.cobro.huboCobo || visit.cobro.montoCobrado <= 0) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-semibold bg-rose-50 text-rose-600" data-id="element-4797" data-mp="187iobwiwp7m2">
          No cobró
        </span>;
    }
    const effectiveAmount = getEffectiveCobro(visit);
    const discount = getVisitDiscount(visit);
    return <div className="text-right" data-id="element-4798" data-mp="1fqjj9tfn5sxt">
        <span className="font-semibold text-emerald-600" data-id="element-4799" data-mp="1fpqbcp4y4xe3">
          {formatCurrency(effectiveAmount)}
        </span>
        {discount > 0 && <span className="block text-[10px] text-emerald-500 font-medium" data-id="element-4800" data-mp="1kjufz1qqotc0">
            PP: -{formatCurrency(discount)}
          </span>}
      </div>;
  };
  // FIXED: Render venta cell with proper logic
  const renderVentaCell = (visit: Visit) => {
    const tieneVentaTipo = visit.tipoVisita === 'venta' || visit.tipoVisita === 'cobro_y_venta';
    if (!tieneVentaTipo) {
      return <span className="text-slate-300" data-id="element-4801" data-mp="11t3d8j19c16l">—</span>;
    }
    // FIXED: Check both huboVenta AND montoVenta > 0
    if (!visit.venta.huboVenta || visit.venta.montoVenta <= 0) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-semibold bg-amber-50 text-amber-600" data-id="element-4802" data-mp="2d5xgjl3bnjf1">
          No vendió
        </span>;
    }
    return <span className="font-semibold text-indigo-600" data-id="element-4803" data-mp="33a42fqjq1lf8">
        {formatCurrency(visit.venta.montoVenta)}
      </span>;
  };
  const isDataLoading = isLoading && visits.length === 0;
  return <div className="space-y-6" data-id="element-4804" data-mp="3d05n08cv8lpe">
      <PageHeader title="Historial de Visitas" subtitle={`${totalCount} registros encontrados`} icon={<MapPinIcon className="w-6 h-6" data-id="element-4806" data-mp="3mxa3brk15mya" />} data-id="element-4805" data-mp="3d1716lp6hobc">
        
        <Button variant="secondary" size="sm" onClick={() => fetchVisits()} isLoading={isLoading} leftIcon={<ClockIcon className="w-4 h-4" data-id="element-4808" data-mp="3vxzswdip6h3k" />} data-id="element-4807" data-mp="3mvyxlm70y54j">
          
          Recargar
        </Button>
      </PageHeader>

      {/* Filters */}
      <Card className="animate-stagger-1" data-id="element-4809" data-mp="3qzydc2khczwu">
        <div className="flex items-center gap-2 mb-4" data-id="element-4810" data-mp="3qwsmahsajnyh">
          <FilterIcon className="w-4 h-4 text-slate-400" data-id="element-4811" data-mp="21l0akcmxn8sl" />
          <span className="text-sm font-semibold text-slate-700" data-id="element-4812" data-mp="1wkvs4h2gs3zq">Filtros</span>
          {hasActiveFilters && <span className="text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full font-medium" data-id="element-4813" data-mp="c33ekbpiu4im">
              {[dateFilter, statusFilter, vendorFilter, typeFilter].filter(Boolean).length}{' '}
              activos
            </span>}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-id="element-4814" data-mp="7bbjpozc1cmo">
          <div className="col-span-1 min-w-0" data-id="element-4815" data-mp="3phpxnnxlpml2">
            <Input type="date" value={dateFilter} onChange={e => {
            setDateFilter(e.target.value);
            setCurrentPage(1);
          }} leftIcon={<CalendarIcon className="w-4 h-4" data-id="element-4817" data-mp="3giur2aqnaah8" />} label="Fecha" data-id="element-4816" data-mp="3pgopbr7yze71" />
            
          </div>
          {isAdmin && <div className="col-span-1 min-w-0" data-id="element-4818" data-mp="1epor73nff6t0">
              <Select label="Vendedor" options={vendorOptions} value={vendorFilter} onChange={e => {
            setVendorFilter(e.target.value);
            setCurrentPage(1);
          }} data-id="element-4819" data-mp="1eqzvr5op4il6" />
            
            </div>}
          <div className="col-span-1 min-w-0" data-id="element-4820" data-mp="7pntz2z8i4ta">
            <Select label="Tipo de Visita" options={typeOptions} value={typeFilter} onChange={e => {
            setTypeFilter(e.target.value);
            setCurrentPage(1);
          }} data-id="element-4821" data-mp="3p5dvn9gqxx0n" />
            
          </div>
          <div className="col-span-1 min-w-0" data-id="element-4822" data-mp="lmsygyjauvmf">
            <Select label="Estado" options={statusOptions} value={statusFilter} onChange={e => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }} data-id="element-4823" data-mp="qmp0is8fsczc" />
            
          </div>
          <div className="col-span-2 lg:col-span-1 flex items-end" data-id="element-4824" data-mp="2wgfa02df0cql">
            <Button variant="secondary" onClick={clearFilters} className="w-full" disabled={!hasActiveFilters} data-id="element-4825" data-mp="31frvojsjvdrl">
              
              Limpiar
            </Button>
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-stagger-1" data-id="element-4826" data-mp="1el69nzu3mow4">
        <StatsCard title="Total Visitas" value={totalCount} icon={<ClipboardListIcon className="w-5 h-5" data-id="element-4828" data-mp="1oikjvhpfihfz" />} variant="primary" data-id="element-4827" data-mp="1si1tmnbw20qy" />
        
        <StatsCard title="Total Cobrado" value={formatCurrency(totalCobrado)} icon={<DollarSignIcon className="w-5 h-5" data-id="element-4830" data-mp="vtvgfc0wi012" />} variant="success" data-id="element-4829" data-mp="10sq7t2cxowws" />
        
        <StatsCard title="Total Vendido" value={formatCurrency(totalVendido)} icon={<ShoppingCartIcon className="w-5 h-5" data-id="element-4832" data-mp="14s7f81bx844l" />} variant="warning" data-id="element-4831" data-mp="19l0litn1fkis" />
        
        <StatsCard title="Tiempo Promedio" value={`${tiempoPromedio} min`} icon={<ClockIcon className="w-5 h-5" data-id="element-4834" data-mp="d11dxhkgs0dq" />} variant="default" data-id="element-4833" data-mp="h2wgepe4sjt3" />
        
      </div>

      {/* Mobile Card List */}
      <div className="md:hidden space-y-3 animate-stagger-2" data-id="element-4835" data-mp="2c6b4313w4fn6">
        {isDataLoading ? <Card data-id="element-4836" data-mp="2h4mf3f5x8w6z">
            <div className="p-8 text-center" data-id="element-4837" data-mp="2h44e658bdtsq">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-slate-100 rounded-2xl mb-3" data-id="element-4838" data-mp="2lw47hetvpogc">
                <ClipboardListIcon className="w-7 h-7 text-slate-400 animate-spin" data-id="element-4839" data-mp="2qw0dtkq1trz2" />
              </div>
              <h3 className="text-base font-semibold text-slate-900 mb-1" data-id="element-4840" data-mp="1t91nrstv3ia5">
                Cargando visitas...
              </h3>
            </div>
          </Card> : filteredVisits.length === 0 ? <Card data-id="element-4841" data-mp="2354m0nllewrt">
            <div className="p-8 text-center" data-id="element-4842" data-mp="234l5nbblcgfw">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-slate-100 rounded-2xl mb-3" data-id="element-4843" data-mp="284zeuwoxhwfi">
                <ClipboardListIcon className="w-7 h-7 text-slate-400" data-id="element-4844" data-mp="3elun5sb5syek" />
              </div>
              <h3 className="text-base font-semibold text-slate-900 mb-1" data-id="element-4845" data-mp="3jk5zc9oxfkzy">
                No hay visitas
              </h3>
              <p className="text-slate-500 text-sm" data-id="element-4846" data-mp="17w13amrz7rn9">
                {hasActiveFilters ? 'No se encontraron visitas con los filtros aplicados' : 'Aún no tienes visitas registradas'}
              </p>
              {visits.length > 0 && filteredVisits.length === 0 && <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100 text-left" data-id="element-4847" data-mp="t8y1w50j8yun">
                  <p className="text-xs text-amber-800 font-bold mb-1" data-id="element-4848" data-mp="oa4pyh0w9fwl">
                    Nota de depuración:
                  </p>
                  <p className="text-[10px] text-amber-700" data-id="element-4849" data-mp="k66r87fhe742">
                    Hay {visits.length} visitas en total, pero los filtros (o tu
                    permiso de vendedor) están ocultando todas. Tu ID:{' '}
                    {user?.id} | Admin: {isAdmin ? 'Sí' : 'No'}
                  </p>
                </div>}
            </div>
          </Card> : paginatedVisits.map(visit => {
        const tieneCobroTipo = visit.tipoVisita === 'cobro' || visit.tipoVisita === 'cobro_y_venta';
        const tieneVentaTipo = visit.tipoVisita === 'venta' || visit.tipoVisita === 'cobro_y_venta';
        const effectiveCobro = getEffectiveCobro(visit);
        const discount = getVisitDiscount(visit);
        return <Card key={visit.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedVisit(visit)} data-id="element-4850" data-mp="x99dxdoydksx">
              
                <div className="flex items-start justify-between gap-3 mb-3" data-id="element-4851" data-mp="ne7ki5nn9wkj">
                  <div className="flex-1 min-w-0" data-id="element-4852" data-mp="9mavwbl6q4sw">
                    <p className="font-semibold text-slate-900 truncate" data-id="element-4853" data-mp="9jezirozjo6k">
                      {visit.clienteNombre}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5" data-id="element-4854" data-mp="3vthjv605gnom">
                      {visit.zona}
                    </p>
                    {isAdmin && <p className="text-xs text-indigo-600 font-medium mt-0.5 flex items-center gap-1" data-id="element-4855" data-mp="4nzmu32prybq">
                        <UserIcon className="w-3 h-3" data-id="element-4856" data-mp="3d45qyytkwhix" />
                        {visit.vendedorNombre}
                      </p>}
                  </div>
                  <div className="flex flex-col items-end gap-1.5 flex-shrink-0" data-id="element-4857" data-mp="3r58e7ewb7xvf">
                    {getStatusBadge(visit.estado)}
                    {getVisitTypeBadge(visit.tipoVisita)}
                  </div>
                </div>

                <div className="flex items-center gap-2 text-xs text-slate-500 mb-3" data-id="element-4858" data-mp="3w39whxlwvt5u">
                  <CalendarIcon className="w-3.5 h-3.5" data-id="element-4859" data-mp="3w175pbcxpw02" />
                  <span data-id="element-4860" data-mp="dnpnfmmkycrc">{formatDate(visit.fecha)}</span>
                  <span data-id="element-4861" data-mp="8q7maiet11tw">•</span>
                  <ClockIcon className="w-3.5 h-3.5" data-id="element-4862" data-mp="8qz8j2mihn7i" />
                  <span data-id="element-4863" data-mp="nn83vt3a7gen">{formatTime(visit.horaLlegada)}</span>
                  {visit.tiempoTotal && <>
                      <span data-id="element-4864" data-mp="iowshdxc8xub">•</span>
                      <span data-id="element-4865" data-mp="ipoqeveer7nr">{visit.tiempoTotal} min</span>
                    </>}
                </div>

                <div className="grid grid-cols-2 gap-2" data-id="element-4866" data-mp="pp8t6iwvc7cy">
                  {tieneCobroTipo && <div className={`p-2.5 rounded-xl ${(visit.cobro as any)?.anulado ? 'bg-slate-50' : 'bg-emerald-50'}`} data-id="element-4867" data-mp="uobxg150y9jv">
                  
                      <p className={`text-xs font-medium mb-0.5 ${(visit.cobro as any)?.anulado ? 'text-slate-400' : 'text-emerald-600'}`} data-id="element-4868" data-mp="14h9jgh986gn8">
                    
                        Cobro
                      </p>
                      <p className="font-bold" data-id="element-4869" data-mp="1dizavn33xfl0">
                        {(visit.cobro as any)?.anulado ? <span className="inline-flex items-center gap-1 text-slate-500 text-sm" data-id="element-4870" data-mp="1idwg0ix9n4sm">
                            <BanIcon className="w-3 h-3" data-id="element-4871" data-mp="1nczm86ozfh26" /> Anulado
                          </span> : visit.cobro.huboCobo && visit.cobro.montoCobrado > 0 ? <>
                            <span className="text-emerald-700" data-id="element-4872" data-mp="3hmxs5mw39zz0">
                              {formatCurrency(effectiveCobro)}
                            </span>
                            {discount > 0 && <span className="block text-[10px] text-emerald-500 font-medium mt-0.5" data-id="element-4873" data-mp="37x4jatnbzyyg">
                                PP: -{formatCurrency(discount)}
                              </span>}
                          </> : <span className="text-rose-500 text-sm" data-id="element-4874" data-mp="4fw0o7ks4v2o">
                            No cobró
                          </span>}
                      </p>
                    </div>}
                  {tieneVentaTipo && <div className="p-2.5 bg-indigo-50 rounded-xl" data-id="element-4875" data-mp="3lp156fyepcme">
                      <p className="text-xs text-indigo-600 font-medium mb-0.5" data-id="element-4876" data-mp="3lqvrnypbtcsl">
                        Venta
                      </p>
                      <p className="font-bold text-indigo-700" data-id="element-4877" data-mp="2a8ulyryhpzdo">
                        {visit.venta.huboVenta && visit.venta.montoVenta > 0 ? formatCurrency(visit.venta.montoVenta) : <span className="text-amber-500 text-sm" data-id="element-4878" data-mp="34wuvw2zm4nh9">
                            No vendió
                          </span>}
                      </p>
                    </div>}
                </div>

                {visit.productosVencidos !== null && visit.productosVencidos !== undefined && <div className={`mt-3 flex items-center gap-2 text-xs font-medium px-2.5 py-1.5 rounded-lg ${visit.productosVencidos ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'}`} data-id="element-4879" data-mp="3iq2mbrbyjcxd">
                
                      {visit.productosVencidos ? <>
                          <AlertTriangleIcon className="w-3.5 h-3.5" data-id="element-4880" data-mp="3duws858eiry7" />
                          Tiene productos vencidos
                        </> : <>
                          <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-4881" data-mp="a9h9u0k4a1pi" />
                          Sin productos vencidos
                        </>}
                    </div>}

                {visit.motivoNoPago && <p className="mt-2 text-xs text-rose-600 bg-rose-50 px-2.5 py-1.5 rounded-lg" data-id="element-4882" data-mp="24oem1583dp4b">
                    ⚠ {visit.motivoNoPago}
                  </p>}
              </Card>;
      })}
      </div>

      {/* Desktop Table */}
      <Card padding="none" className="hidden md:block animate-stagger-2" data-id="element-4883" data-mp="1c092glsry662">
        {isDataLoading ? <div className="p-16 text-center animate-fade-in" data-id="element-4884" data-mp="17277b2i2qge8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-4885" data-mp="1lyg662y1yrq4">
              <ClipboardListIcon className="w-8 h-8 text-slate-400 animate-spin" data-id="element-4886" data-mp="1gyk9ypvlk4l4" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-4887" data-mp="2xcaf7zygcywc">
              Cargando visitas...
            </h3>
          </div> : filteredVisits.length === 0 ? <div className="p-16 text-center animate-fade-in" data-id="element-4888" data-mp="370a9s948ynms">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-4889" data-mp="371v4byqka6kn">
              <ClipboardListIcon className="w-8 h-8 text-slate-400" data-id="element-4890" data-mp="323jqlcx5bbx5" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-4891" data-mp="2x2nq79c067wh">
              No hay visitas
            </h3>
            <p className="text-slate-500 text-sm mb-4" data-id="element-4892" data-mp="2t3578j3qs6wa">
              {hasActiveFilters ? 'No se encontraron visitas con los filtros aplicados' : 'Aún no tienes visitas registradas'}
            </p>
            {visits.length > 0 && filteredVisits.length === 0 && <div className="max-w-xs mx-auto p-3 bg-amber-50 rounded-lg border border-amber-100 text-left" data-id="element-4893" data-mp="3fz5f7izgvtp1">
                <p className="text-xs text-amber-800 font-bold mb-1" data-id="element-4894" data-mp="3bxtw7wb3m6pt">
                  Nota de depuración:
                </p>
                <p className="text-[10px] text-amber-700" data-id="element-4895" data-mp="7kwtmuvd436d">
                  Hay {visits.length} visitas en total, pero los filtros (o tu
                  permiso de vendedor) están ocultando todas. Tu ID: {user?.id}{' '}
                  | Admin: {isAdmin ? 'Sí' : 'No'}
                </p>
              </div>}
          </div> : <div className="overflow-x-auto" data-id="element-4896" data-mp="1z7bd33bat3e1">
            <table className="w-full" data-id="element-4897" data-mp="247xw27pgp8km">
              <thead data-id="element-4898" data-mp="248qxqu9kd7vw">
                <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-4899" data-mp="24994iktur4i2">
                  <SortableTableHeader label="Fecha" sortKey="fecha" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-4900" data-mp="29699eseyu17l" />
                
                  {isAdmin && <SortableTableHeader label="Vendedor" sortKey="vendedor" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-4901" data-mp="2mx5odgfyyjw6" />}
                  <SortableTableHeader label="Cliente" sortKey="cliente" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-4902" data-mp="wtly7r4hqbw4" />
                
                  <SortableTableHeader label="Tipo" sortKey="tipo" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-4903" data-mp="11tqfvkh4xcnx" />
                
                  <SortableTableHeader label="Cobro" sortKey="cobro" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-4904" data-mp="1pistmh8neld5" />
                
                  <SortableTableHeader label="Venta" sortKey="venta" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-4905" data-mp="3mbun4r4oaaov" />
                
                  <SortableTableHeader label="Tiempo" sortKey="tiempo" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-4906" data-mp="1s40irm7zsep5" />
                
                  <SortableTableHeader label="Estado" sortKey="estado" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-4907" data-mp="1o25iml1ss7cy" />
                
                  <th className="px-5 py-4 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-4908" data-mp="33jpa0psa3zvr">
                    Vencidos
                  </th>
                  <th className="px-5 py-4 w-12" data-id="element-4909" data-mp="18f7sbx9whlhj"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-4910" data-mp="14e5of351gl98">
                {paginatedVisits.map(visit => <tr key={visit.id} className="hover:bg-slate-50/50 transition-colors cursor-pointer" onClick={() => setSelectedVisit(visit)} data-id="element-4911" data-mp="ze9trui8e66c">
                
                    <td className="px-5 py-4" data-id="element-4912" data-mp="pgvcxtsebfdi">
                      <p className="font-medium text-slate-900 text-sm" data-id="element-4913" data-mp="pipztdn89hk8">
                        {formatDate(visit.fecha)}
                      </p>
                      <p className="text-xs text-slate-400" data-id="element-4914" data-mp="gniekcbkblk1">
                        {formatTime(visit.horaLlegada)}
                      </p>
                    </td>
                    {isAdmin && <td className="px-5 py-4 text-sm text-slate-700" data-id="element-4915" data-mp="6qnis16obz9g">
                        {visit.vendedorNombre}
                      </td>}
                    <td className="px-5 py-4" data-id="element-4916" data-mp="1tditrlns57d">
                      <p className="font-medium text-slate-900 text-sm" data-id="element-4917" data-mp="3nrgkcb34ho3n">
                        {visit.clienteNombre}
                      </p>
                      <p className="text-xs text-slate-400" data-id="element-4918" data-mp="3sto1df2t7ucu">{visit.zona}</p>
                    </td>
                    <td className="px-5 py-4 text-center" data-id="element-4919" data-mp="1j7axo9ltuze">
                      {getVisitTypeBadge(visit.tipoVisita)}
                    </td>
                    <td className="px-5 py-4 text-right" data-id="element-4920" data-mp="352s2xz3um0wm">
                      {renderCobroCell(visit)}
                    </td>
                    <td className="px-5 py-4 text-right" data-id="element-4921" data-mp="3a1lg1qf83pw9">
                      {renderVentaCell(visit)}
                    </td>
                    <td className="px-5 py-4 text-center text-sm text-slate-600" data-id="element-4922" data-mp="3eybuk9bdqdfk">
                      {visit.tiempoTotal ? `${visit.tiempoTotal} min` : <span className="text-slate-300" data-id="element-4923" data-mp="2mazjavao5s1k">—</span>}
                    </td>
                    <td className="px-5 py-4 text-center" data-id="element-4924" data-mp="2r8rdxx2p7s9g">
                      {getStatusBadge(visit.estado)}
                    </td>
                    <td className="px-5 py-4 text-center" data-id="element-4925" data-mp="2eb30f76jixgf">
                      {visit.productosVencidos === true ? <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-semibold bg-rose-50 text-rose-600" data-id="element-4926" data-mp="2t08wwndzu9km">
                          Sí
                        </span> : visit.productosVencidos === false ? <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-600" data-id="element-4927" data-mp="20badlmy4x1dq">
                          No
                        </span> : <span className="text-slate-300" data-id="element-4928" data-mp="2aaj1pz6bjls6">—</span>}
                    </td>
                    <td className="px-5 py-4" data-id="element-4929" data-mp="25c7vrzj59vf8">
                      <Button variant="ghost" size="sm" onClick={e => {
                  e.stopPropagation();
                  setSelectedVisit(visit);
                }} data-id="element-4930" data-mp="3lnlk8zrzalez">
                    
                        <EyeIcon className="w-4 h-4 text-slate-400" data-id="element-4931" data-mp="3t7w7t6csrmpz" />
                      </Button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </Card>

      {/* Pagination */}
      {filteredVisits.length > 0 && <div className="animate-stagger-3" data-id="element-4932" data-mp="atwo86kxaz2h">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={totalCount} itemsPerPage={ITEMS_PER_PAGE} data-id="element-4933" data-mp="av7se7ia68u4" />
        
        </div>}

      {/* Detail Modal */}
      <Modal isOpen={!!selectedVisit} onClose={() => setSelectedVisit(null)} title="Detalle de Visita" size="lg" data-id="element-4934" data-mp="62y7x6zxq0z8">
        
        {selectedVisit && <>
            <ModalBody data-id="element-4935" data-mp="yj8xf6g9374s">
              <div className="space-y-6" data-id="element-4936" data-mp="ykixu2aksrel">
                <div className="flex items-start justify-between pb-4 border-b border-slate-100" data-id="element-4937" data-mp="yllp4bbt8vv1">
                  <div data-id="element-4938" data-mp="9uxviv6iyb6l">
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-4939" data-mp="52egruw3pa07">
                      {selectedVisit.clienteNombre}
                    </h3>
                    <p className="text-sm text-slate-500" data-id="element-4940" data-mp="3tc9j9tg45y">
                      {selectedVisit.zona}
                    </p>
                  </div>
                  {getStatusBadge(selectedVisit.estado)}
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-4941" data-mp="spmiy3y8g2y0">
                  <div className="p-4 bg-slate-50 rounded-xl" data-id="element-4942" data-mp="ntnru8lis6tw">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-4943" data-mp="it9hgjwg4ksp">Vendedor</p>
                    <p className="font-semibold text-slate-900" data-id="element-4944" data-mp="ivm7lkccy1gu">
                      {selectedVisit.vendedorNombre}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl" data-id="element-4945" data-mp="33kjqumjgrpj9">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-4946" data-mp="2ykxfkin74jd3">
                      Tipo de Visita
                    </p>
                    <p className="font-semibold text-slate-900" data-id="element-4947" data-mp="1e3o4clnf6zow">
                      {getVisitTypeLabel(selectedVisit.tipoVisita)}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl" data-id="element-4948" data-mp="1o0aof87x9mpv">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-4949" data-mp="1nyzm7cu4kn0v">Fecha</p>
                    <p className="font-semibold text-slate-900" data-id="element-4950" data-mp="1st3uq0j1h342">
                      {formatDate(selectedVisit.fecha)}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl" data-id="element-4951" data-mp="21rfuot5spd96">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-4952" data-mp="21u21urorxqqv">Horario</p>
                    <p className="font-semibold text-slate-900" data-id="element-4953" data-mp="26s3i74utfbyn">
                      {formatTime(selectedVisit.horaLlegada)} -{' '}
                      {formatTime(selectedVisit.horaSalida)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-6 p-4 bg-[#eef2fc] rounded-xl" data-id="element-4954" data-mp="hbkn7vyfedzp">
                  <div className="flex items-center gap-2" data-id="element-4955" data-mp="3ddjqz1rr0aol">
                    <ClockIcon className="w-5 h-5 text-[#2952C4]" data-id="element-4956" data-mp="3df4jklui5jjt" />
                    <div data-id="element-4957" data-mp="3iatkotm678i8">
                      <p className="text-xs text-[#2952C4] font-medium" data-id="element-4958" data-mp="3ie7p07ee9rsb">
                        Tiempo Total
                      </p>
                      <p className="font-bold text-[#0D1B46]" data-id="element-4959" data-mp="3rdba1gx4gh9g">
                        {selectedVisit.tiempoTotal || 0} minutos
                      </p>
                    </div>
                  </div>
                </div>

                {(selectedVisit.tipoVisita === 'cobro' || selectedVisit.tipoVisita === 'cobro_y_venta') && (() => {
              const isAnulado = (selectedVisit.cobro as any)?.anulado;
              const huboCobro = selectedVisit.cobro.huboCobo && selectedVisit.cobro.montoCobrado > 0;
              const credito = selectedVisit.cobro.credito || 0;
              const descuento = getVisitDiscount(selectedVisit);
              const effectiveAmount = getEffectiveCobro(selectedVisit);
              const facturasDetalle: any[] = (selectedVisit.cobro as any)?.facturasDetalle || [];
              const bgClass = isAnulado ? 'bg-slate-50 border-slate-200' : huboCobro ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200';
              const titleColor = isAnulado ? 'text-slate-500' : huboCobro ? 'text-emerald-800' : 'text-rose-800';
              return <div className={`p-4 rounded-xl border ${bgClass}`} data-id="element-4960" data-mp="1fqhftoa23ll8">
                        <h4 className={`font-semibold mb-3 flex items-center gap-2 ${titleColor}`} data-id="element-4961" data-mp="1boe3kxfkccyw">
                      
                          {isAnulado ? <>
                              <BanIcon className="w-5 h-5" data-id="element-4962" data-mp="11qpybdkbzn2h" /> Cobro Anulado
                            </> : huboCobro ? <>
                              <DollarSignIcon className="w-5 h-5" data-id="element-4963" data-mp="x0vxpvq9lcaw" /> Cobro
                              Realizado
                            </> : <>
                              <DollarSignIcon className="w-5 h-5" data-id="element-4964" data-mp="i5zk12zorwqf" /> Sin Cobro
                            </>}
                        </h4>
                        {isAnulado && huboCobro ? <div className="space-y-3" data-id="element-4965" data-mp="1auwnw135hqzn">
                            <div className="p-3 bg-slate-100 rounded-lg border border-slate-200" data-id="element-4966" data-mp="1fqlrqgm7du1r">
                              <p className="text-sm text-slate-500 font-medium flex items-center gap-1.5" data-id="element-4967" data-mp="111pnyvr6j36y">
                                <BanIcon className="w-4 h-4" data-id="element-4968" data-mp="160iziimwok4h" />
                                Este cobro fue anulado. Los montos no se
                                contabilizan en los totales.
                              </p>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm opacity-60" data-id="element-4969" data-mp="3j8a58s9cawuh">
                              <div data-id="element-4970" data-mp="3e9gwfd4zzu0n">
                                <p className="text-slate-500" data-id="element-4971" data-mp="3e8fjt48c2zgt">Monto Original</p>
                                <p className="font-bold text-slate-600 line-through" data-id="element-4972" data-mp="3syv6kngeefh3">
                                  {formatCurrency(selectedVisit.cobro.montoCobrado)}
                                </p>
                              </div>
                              <div data-id="element-4973" data-mp="2vbe9r4zxa960">
                                <p className="text-slate-500" data-id="element-4974" data-mp="2vc5xxur6x4m9">Método de Pago</p>
                                <p className="font-bold text-slate-600" data-id="element-4975" data-mp="39b5j2sfhv1do">
                                  {getPaymentMethodLabel(selectedVisit.cobro.metodoPago)}
                                </p>
                              </div>
                            </div>
                          </div> : huboCobro ? <div className="space-y-3" data-id="element-4976" data-mp="1fti0w907tlbu">
                            <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-4977" data-mp="1fv2spqv5aq60">
                              <div data-id="element-4978" data-mp="1julf6r2m79ay">
                                <p className="text-emerald-600" data-id="element-4979" data-mp="1oto8je98fr33">
                                  Monto Cobrado
                                </p>
                                <p className="font-bold text-emerald-900" data-id="element-4980" data-mp="1tu2ij1ue9b3r">
                                  {formatCurrency(effectiveAmount)}
                                </p>
                              </div>
                              <div data-id="element-4981" data-mp="22rcvqsqrvabb">
                                <p className="text-emerald-600" data-id="element-4982" data-mp="27k5yxc6leala">
                                  Método de Pago
                                </p>
                                <p className="font-bold text-emerald-900" data-id="element-4983" data-mp="2cih9xq8mir53">
                                  {getPaymentMethodLabel(selectedVisit.cobro.metodoPago)}
                                </p>
                              </div>
                              {credito > 0 && <div data-id="element-4984" data-mp="5uqbssjk8rcp">
                                  <p className="text-emerald-600" data-id="element-4985" data-mp="5tozyluq00tx">
                                    Crédito (Abono)
                                  </p>
                                  <p className="font-bold text-emerald-900" data-id="element-4986" data-mp="xgn5f3zwjpx">
                                    {formatCurrency(credito)}
                                  </p>
                                </div>}
                              {descuento > 0 && <div data-id="element-4987" data-mp="onb1k13yqgca">
                                  <p className="text-emerald-600" data-id="element-4988" data-mp="jtzrlr3utjg5">
                                    Descuento P.P.
                                  </p>
                                  <p className="font-bold text-amber-600" data-id="element-4989" data-mp="ev50m1vqgoky">
                                    -{formatCurrency(descuento)}
                                  </p>
                                </div>}
                              <div data-id="element-4990" data-mp="31s6cgv9uiki2">
                                <p className="text-emerald-600" data-id="element-4991" data-mp="31t8yaosb9wr4">Factura</p>
                                <p className="font-bold text-emerald-900" data-id="element-4992" data-mp="36raixe5dy44p">
                                  {selectedVisit.cobro.numeroFactura || '-'}
                                </p>
                              </div>
                              <div data-id="element-4993" data-mp="2wzwnpikgvqhd">
                                <p className="text-emerald-600" data-id="element-4994" data-mp="2wyv06em9lbir">Estado</p>
                                <p className="font-bold text-emerald-900" data-id="element-4995" data-mp="2izvf1gxynerc">
                                  {getInvoiceStatusLabel(selectedVisit.cobro.estadoFactura)}
                                </p>
                              </div>
                            </div>
                            {/* Detalle por factura */}
                            {facturasDetalle.length > 0 && <div className="mt-2" data-id="element-4996" data-mp="caqhtwrfriqk">
                                <p className="text-xs font-semibold text-emerald-700 mb-2 uppercase tracking-wide" data-id="element-4997" data-mp="cagrg82hb9kc">
                                  Detalle por Factura
                                </p>
                                <div className="space-y-1.5" data-id="element-4998" data-mp="1rvrfgksnxh9p">
                                  {facturasDetalle.map((fd: any, idx: number) => <div key={idx} className="flex items-center justify-between text-xs bg-white/70 rounded-lg px-3 py-2 border border-emerald-100" data-id="element-4999" data-mp="21r2uz5bwasii" data-mpi={idx}>
                              
                                        <span className="font-semibold text-slate-700" data-id="element-5000" data-mp="2phoqxy8mkmc5">
                                          {fd.numeroFactura}
                                        </span>
                                        <div className="flex items-center gap-3" data-id="element-5001" data-mp="2kgrc9vmtqafe">
                                          {fd.descuento > 0 && <span className="text-amber-600" data-id="element-5002" data-mp="q5ckzae602xj">
                                              PP: -
                                              {formatCurrency(fd.descuento)}
                                            </span>}
                                          <span className="font-bold text-emerald-700" data-id="element-5003" data-mp="e0ot6ttqrbqn">
                                            {formatCurrency(fd.efectivoPagado || fd.abono || 0)}
                                          </span>
                                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${fd.saldoNuevo <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-5004" data-mp="3rg4mxi3wi3gi">
                                  
                                            {fd.saldoNuevo <= 0 ? 'Pagada' : `Saldo: ${formatCurrency(fd.saldoNuevo)}`}
                                          </span>
                                        </div>
                                      </div>)}
                                </div>
                              </div>}
                            {selectedVisit.cobro.metodoPago === 'cheque' && selectedVisit.cobro.chequeImagen && <div className="mt-3" data-id="element-5005" data-mp="2ck3biuv5ldxr">
                                  <p className="text-sm font-semibold text-emerald-700 mb-2" data-id="element-5006" data-mp="2cgnydxl1ieyz">
                                    Imagen del Cheque
                                  </p>
                                  <img src={selectedVisit.cobro.chequeImagen} alt="Cheque adjunto" className="w-full max-h-48 object-contain rounded-xl border border-emerald-200 bg-white" data-id="element-5007" data-mp="23kgbodi2og6t" />
                        
                                </div>}
                          </div> : <div className="text-sm text-rose-700" data-id="element-5008" data-mp="n08nzo8wwk2y">
                            <p data-id="element-5009" data-mp="i3i8b210rqi2">No se realizó cobro en esta visita</p>
                            {selectedVisit.motivoNoPago && <p className="mt-2 p-2 bg-rose-100 rounded-lg text-rose-800" data-id="element-5010" data-mp="vstti2nj8u9u">
                                <strong data-id="element-5011" data-mp="rttbungsjxof">Motivo:</strong>{' '}
                                {selectedVisit.motivoNoPago}
                              </p>}
                          </div>}
                      </div>;
            })()}

                {(selectedVisit.tipoVisita === 'venta' || selectedVisit.tipoVisita === 'cobro_y_venta') && <div className={`p-4 rounded-xl border ${selectedVisit.venta.huboVenta && selectedVisit.venta.montoVenta > 0 ? 'bg-[#eef2fc] border-[#d5e0f7]' : 'bg-amber-50 border-amber-200'}`} data-id="element-5012" data-mp="83hkb2s7gm7j">
                
                    <h4 className={`font-semibold mb-3 flex items-center gap-2 ${selectedVisit.venta.huboVenta && selectedVisit.venta.montoVenta > 0 ? 'text-[#1E3D96]' : 'text-amber-800'}`} data-id="element-5013" data-mp="1jjg1xv1g8gkd">
                  
                      <ShoppingCartIcon className="w-5 h-5" data-id="element-5014" data-mp="1i2ipnf43s8z6" />
                      {selectedVisit.venta.huboVenta && selectedVisit.venta.montoVenta > 0 ? 'Venta Realizada' : 'Sin Venta'}
                    </h4>
                    {selectedVisit.venta.huboVenta && selectedVisit.venta.montoVenta > 0 ? <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-5015" data-mp="2fphljnmz1cw0">
                        <div data-id="element-5016" data-mp="210d2634fx2mb">
                          <p className="text-[#2952C4]" data-id="element-5017" data-mp="20zafk7e5hmc7">Monto de Venta</p>
                          <p className="font-bold text-[#0D1B46]" data-id="element-5018" data-mp="25z6hm13af3p4">
                            {formatCurrency(selectedVisit.venta.montoVenta)}
                          </p>
                        </div>
                        <div data-id="element-5019" data-mp="2yid6zuf0h8lr">
                          <p className="text-[#2952C4]" data-id="element-5020" data-mp="2yillfqqs8lz2">Número de Pedido</p>
                          <p className="font-bold text-[#0D1B46]" data-id="element-5021" data-mp="38c92jalmchh4">
                            {selectedVisit.venta.numeroPedido || '-'}
                          </p>
                        </div>
                      </div> : <p className="text-sm text-amber-700 font-medium" data-id="element-5022" data-mp="2ygfqrtfq4s0e">
                        No se realizó venta en esta visita
                      </p>}
                  </div>}

                {selectedVisit.comentarios && <div data-id="element-5023" data-mp="2km573qil3o17">
                    <p className="text-sm font-medium text-slate-700 mb-2" data-id="element-5024" data-mp="2klvgc0pptcug">
                      Comentarios
                    </p>
                    <p className="text-slate-600 bg-slate-50 p-3 rounded-xl text-sm" data-id="element-5025" data-mp="2ap0h1flmb8f4">
                      {selectedVisit.comentarios}
                    </p>
                  </div>}

                {selectedVisit.productosVencidos !== null && selectedVisit.productosVencidos !== undefined && <div className={`p-4 rounded-xl border flex items-center gap-3 ${selectedVisit.productosVencidos ? 'bg-rose-50 border-rose-200' : 'bg-emerald-50 border-emerald-200'}`} data-id="element-5026" data-mp="h6tarptdsrkh">
                
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${selectedVisit.productosVencidos ? 'bg-rose-100' : 'bg-emerald-100'}`} data-id="element-5027" data-mp="q89j7s0myjm2">
                  
                        <span className="text-base" data-id="element-5028" data-mp="3un0y5jdkv6xz">
                          {selectedVisit.productosVencidos ? '⚠️' : '✅'}
                        </span>
                      </div>
                      <div data-id="element-5029" data-mp="7fotukk5awn0">
                        <p className={`text-xs font-semibold uppercase tracking-wide mb-0.5 ${selectedVisit.productosVencidos ? 'text-rose-500' : 'text-emerald-500'}`} data-id="element-5030" data-mp="7edr8o2fruxh">
                    
                          Productos Vencidos
                        </p>
                        <p className={`font-bold text-sm ${selectedVisit.productosVencidos ? 'text-rose-800' : 'text-emerald-800'}`} data-id="element-5031" data-mp="3fup7wamjw4rq">
                    
                          {selectedVisit.productosVencidos ? 'El cliente tiene productos vencidos' : 'Sin productos vencidos'}
                        </p>
                      </div>
                    </div>}

                {/* Devolucion Items */}
                {selectedVisit.devolucionItems && selectedVisit.devolucionItems.length > 0 && <div className="p-4 rounded-xl border border-rose-200 bg-rose-50" data-id="element-5032" data-mp="20468o9pphm77">
                      <h4 className="font-semibold mb-3 flex items-center gap-2 text-rose-800" data-id="element-5033" data-mp="2e3pgt2iioxji">
                        <PackageIcon className="w-5 h-5" data-id="element-5034" data-mp="2a2vps09s32me" />
                        Productos para Devolución (
                        {selectedVisit.devolucionItems.length})
                      </h4>
                      <div className="space-y-2" data-id="element-5035" data-mp="fq5zdsimcbjr">
                        {selectedVisit.devolucionItems.map((item, idx) => <div key={idx} className="p-3 bg-white rounded-lg border border-rose-100" data-id="element-5036" data-mp="5lpkp2gvl0hj" data-mpi={idx}>
                    
                            <div className="flex items-start justify-between gap-2 mb-2" data-id="element-5037" data-mp="3s1ac396ffc8d">
                              <div data-id="element-5038" data-mp="u7k4firl1x3">
                                <p className="font-semibold text-slate-900 text-sm" data-id="element-5039" data-mp="t65y26gbzb5">
                                  {item.productoNombre}
                                </p>
                                <p className="text-xs text-slate-500 font-mono" data-id="element-5040" data-mp="3n1xo2l3tjz47">
                                  {item.productoSku}
                                </p>
                              </div>
                            </div>
                            <div className="grid grid-cols-4 gap-3 text-sm" data-id="element-5041" data-mp="3e19afric80ru">
                              <div data-id="element-5042" data-mp="17bunmkuojzlw">
                                <p className="text-rose-600 text-xs" data-id="element-5043" data-mp="178gnzkdei4i5">
                                  Cantidad
                                </p>
                                <p className="font-bold text-slate-900" data-id="element-5044" data-mp="19v7qa733tsrp">
                                  {item.cantidad}
                                </p>
                              </div>
                              <div data-id="element-5045" data-mp="10thrgg6w7rjw">
                                <p className="text-rose-600 text-xs" data-id="element-5046" data-mp="10ujezk53i6ii">Precio</p>
                                <p className="font-bold text-slate-900" data-id="element-5047" data-mp="vwhv4wzui35z">
                                  {item.precioUnitario ? formatCurrency(item.precioUnitario) : '-'}
                                </p>
                              </div>
                              <div data-id="element-5048" data-mp="1js3ui0lyzsqg">
                                <p className="text-rose-600 text-xs" data-id="element-5049" data-mp="1jr2eriu0ei2e">Lote</p>
                                <p className="font-bold text-slate-900" data-id="element-5050" data-mp="1etj1om91sbc1">
                                  {item.lote || '-'}
                                </p>
                              </div>
                              <div data-id="element-5051" data-mp="27h56fxu0hm0h">
                                <p className="text-rose-600 text-xs" data-id="element-5052" data-mp="2etvzwuui13er">
                                  F. Vencimiento
                                </p>
                                <p className="font-bold text-slate-900" data-id="element-5053" data-mp="2jtqrl0m3lx0v">
                                  {item.fechaVencimiento ? new Date(item.fechaVencimiento).toLocaleDateString('es-DO', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        }) : '-'}
                                </p>
                              </div>
                            </div>
                          </div>)}
                      </div>
                    </div>}

                {selectedVisit.proximaVisita && <div className="p-3 bg-amber-50 rounded-xl border border-amber-200" data-id="element-5054" data-mp="3hewh7uy9miq8">
                    <p className="text-sm text-amber-700" data-id="element-5055" data-mp="3lg8cafzwrw5t">
                      <span className="font-semibold" data-id="element-5056" data-mp="amu6bht3uyce">Próxima visita:</span>{' '}
                      {formatDate(selectedVisit.proximaVisita)}
                    </p>
                  </div>}
              </div>
            </ModalBody>
            <ModalFooter data-id="element-5057" data-mp="kjp3nxdn6sp3">
              <Button variant="secondary" onClick={() => setSelectedVisit(null)} data-id="element-5058" data-mp="fn866ckjz1zc">
              
                Cerrar
              </Button>
            </ModalFooter>
          </>}
      </Modal>
    </div>;
}